<!--
  Dev-only polygon for the standalone Document PiP tool.
  Student = real getUserMedia camera/mic. Tutor = synthetic animated canvas stream.
  Auth bypass: route-gate.ts whitelists /dev/preview* in dev; 404 guard below for prod.
-->
<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, shallowRef, watchEffect } from 'vue'

import { readCssToken } from '@shared/lib/browser'

import { usePip } from '../../model/pip/pip.hook'

defineOptions({
  name: 'PipPreview',
})

if (!import.meta.dev) {
  throw createError({ statusCode: 404 })
}

const tutorVideoEl = ref<HTMLVideoElement | null>(null)
const studentVideoEl = ref<HTMLVideoElement | null>(null)

const tutorStream = shallowRef<MediaStream | null>(null)
const studentStream = shallowRef<MediaStream | null>(null)

const tutorTrack = shallowRef<MediaStreamTrack | null>(null)
const studentTrack = shallowRef<MediaStreamTrack | null>(null)

const micEnabled = ref<boolean>(true)
const camEnabled = ref<boolean>(true)
const mediaError = ref<string | null>(null)

let tutorAnimationId = 0
let isUnmounted = false

const startTutorStream = (): void => {
  const canvas = document.createElement('canvas')
  canvas.width = 640
  canvas.height = 360
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const background = readCssToken('--btly-surface-inverse')
  const figure = readCssToken('--btly-action-primary-default')
  const caption = readCssToken('--btly-text-inverse')

  const draw = (time: number): void => {
    const t = time / 1000
    ctx.fillStyle = background
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.beginPath()
    ctx.arc(
      canvas.width / 2 + Math.cos(t) * 180,
      canvas.height / 2 + Math.sin(t * 1.4) * 100,
      42,
      0,
      Math.PI * 2,
    )
    ctx.fillStyle = figure
    ctx.fill()
    ctx.fillStyle = caption
    ctx.font = '24px system-ui'
    ctx.fillText('Synthetic tutor cam', 24, 44)
    tutorAnimationId = requestAnimationFrame(draw)
  }

  tutorAnimationId = requestAnimationFrame(draw)
  const stream = canvas.captureStream(30)
  tutorStream.value = stream
  tutorTrack.value = stream.getVideoTracks()[0] ?? null
}

const startStudentStream = async (): Promise<void> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
    if (isUnmounted) {
      stream.getTracks().forEach((track) => track.stop())

      return
    }
    studentStream.value = stream
    studentTrack.value = stream.getVideoTracks()[0] ?? null
  } catch (error) {
    mediaError.value = 'Camera/microphone access failed — allow permissions and reload.'
    console.warn('[pip-preview] getUserMedia failed', error)
  }
}

const toggleMic = (): void => {
  micEnabled.value = !micEnabled.value
  const audioTrack = studentStream.value?.getAudioTracks()[0]
  if (audioTrack) {
    audioTrack.enabled = micEnabled.value
  }
}

const toggleCam = (): void => {
  camEnabled.value = !camEnabled.value
  const videoTrack = studentStream.value?.getVideoTracks()[0]
  if (videoTrack) {
    videoTrack.enabled = camEnabled.value
  }
  studentTrack.value = camEnabled.value ? (videoTrack ?? null) : null
}

const { isSupported, isActive, toggle } = usePip({
  tutorTrack,
  studentTrack,
  micEnabled,
  camEnabled,
  onToggleMic: toggleMic,
  onToggleCam: toggleCam,
  labels: { tutor: 'Tutor', student: 'You' },
})

const togglePip = (): void => {
  void toggle()
}

watchEffect(() => {
  if (tutorVideoEl.value) {
    tutorVideoEl.value.srcObject = tutorStream.value
  }
})

watchEffect(() => {
  if (studentVideoEl.value) {
    studentVideoEl.value.srcObject = studentStream.value
  }
})

onMounted(() => {
  startTutorStream()
  void startStudentStream()
})

onBeforeUnmount(() => {
  isUnmounted = true
  cancelAnimationFrame(tutorAnimationId)
  tutorStream.value?.getTracks().forEach((track) => track.stop())
  studentStream.value?.getTracks().forEach((track) => track.stop())
})
</script>

<template>
  <div class="pip-preview">
    <h1 class="pip-preview__title">
      Standalone PiP — dev preview
    </h1>

    <p
      v-if="mediaError"
      class="pip-preview__error"
    >
      {{ mediaError }}
    </p>

    <div class="pip-preview__tiles">
      <div class="pip-preview__tile">
        <video
          ref="tutorVideoEl"
          autoplay
          playsinline
          muted
        />
        <span class="pip-preview__label">Tutor (synthetic)</span>
      </div>
      <div
        class="pip-preview__tile"
        :class="{ 'pip-preview__tile--off': !camEnabled }"
      >
        <video
          ref="studentVideoEl"
          autoplay
          playsinline
          muted
        />
        <span class="pip-preview__label">You (getUserMedia)</span>
      </div>
    </div>

    <div class="pip-preview__controls">
      <button
        type="button"
        class="pip-preview__button"
        @click="toggleMic"
      >
        Mic: {{ micEnabled ? 'on' : 'off' }}
      </button>
      <button
        type="button"
        class="pip-preview__button"
        @click="toggleCam"
      >
        Cam: {{ camEnabled ? 'on' : 'off' }}
      </button>
      <button
        v-if="isSupported"
        type="button"
        class="pip-preview__button pip-preview__button--primary"
        @click="togglePip"
      >
        {{ isActive ? 'Close PiP' : 'Open PiP' }}
      </button>
      <p
        v-else
        class="pip-preview__unsupported"
      >
        Document PiP is not supported in this browser — the button stays hidden (by design).
      </p>
    </div>

    <p class="pip-preview__status">
      PiP active: {{ isActive }}
    </p>
  </div>
</template>

<style scoped lang="scss" src="./PipPreview.scss" />
